import { ArrowLeft } from 'lucide-react'
import type { MetaFunction } from 'react-router'
import { Link, useParams } from 'react-router'
import FooterSection from '#app/components/Landing-page/footer-section'
import { ArticleHeader } from '#app/components/blog/ArticleHeader'
import { RelatedPosts } from '#app/components/blog/RelatedPosts'
import { AnimatedButton } from '#app/components/ui/animated-button'
import { getBlogPost, getRelatedPosts } from '#app/data/blog'

export const meta: MetaFunction = ({ params }) => {
	const post = getBlogPost(params.slug ?? '')
	if (!post) return [{ title: 'Post Not Found — TrueTenant' }]

	return [
		{ title: `${post.title} — TrueTenant Blog` },
		{ name: 'description', content: post.excerpt },
		{ property: 'og:title', content: post.title },
		{ property: 'og:description', content: post.excerpt },
		{ property: 'og:image', content: post.coverImage },
		{ property: 'og:type', content: 'article' },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: post.title },
		{ name: 'twitter:description', content: post.excerpt },
	]
}

export default function BlogPostRoute() {
	const { slug } = useParams()
	const post = getBlogPost(slug ?? '')

	if (!post) {
		return (
			<div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 px-4">
				<h1 className="font-['Futura_Hv_BT'] text-[32px] text-[#32302F]">
					Post not found
				</h1>
				<AnimatedButton
					asChild
					className="rounded-[52px] bg-[#32302F] text-white hover:bg-[#1a1918]"
				>
					<Link to="/blog">Back to Blog</Link>
				</AnimatedButton>
			</div>
		)
	}

	const relatedPosts = getRelatedPosts(post.slug, post.category)

	return (
		<div>
			{/* Back link */}
			<div className="px-4 pt-8 md:px-[100px]">
				<Link
					to="/blog"
					className="inline-flex items-center gap-2 text-sm text-[#747474] transition-colors hover:text-[#32302F]"
				>
					<ArrowLeft size={16} />
					Back to Blog
				</Link>
			</div>

			{/* Article header */}
			<div className="px-4 py-10 md:px-[100px]">
				<ArticleHeader post={post} />
			</div>

			{/* Cover image */}
			<div className="px-4 pb-10 md:px-[100px]">
				<div className="mx-auto max-w-3xl overflow-hidden rounded-2xl">
					<img
						src={post.coverImage}
						alt={post.title}
						className="h-auto w-full object-cover"
					/>
				</div>
			</div>

			{/* Article body */}
			<div className="px-4 pb-12 md:px-[100px]">
				<div className="mx-auto max-w-3xl">
					<div
						className="prose prose-lg max-w-none
							prose-headings:font-['Futura_Hv_BT'] prose-headings:text-[#32302F]
							prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-[24px] md:prose-h2:text-[28px]
							prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-[20px]
							prose-p:text-[#32302F] prose-p:leading-relaxed
							prose-a:text-[#32302F] prose-a:underline hover:prose-a:no-underline
							prose-strong:text-[#32302F]
							prose-ul:text-[#32302F] prose-ol:text-[#32302F]
							prose-li:my-1
							prose-table:border-collapse prose-th:border prose-th:border-[#AEAEAE] prose-th:bg-[#F8F9FA] prose-th:p-3 prose-th:text-left prose-th:text-[#32302F]
							prose-td:border prose-td:border-[#AEAEAE] prose-td:p-3 prose-td:text-[#32302F]
							prose-blockquote:border-l-4 prose-blockquote:border-[#32302F] prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-[#747474]
						"
					>
						{post.content.split('\n').map((line, i) => {
							const trimmed = line.trim()
							if (!trimmed) return null

							if (trimmed.startsWith('## ')) {
								return (
									<h2
										key={i}
										className="mt-10 mb-4 font-['Futura_Hv_BT'] text-[24px] text-[#32302F] md:text-[28px]"
									>
										{trimmed.slice(3)}
									</h2>
								)
							}
							if (trimmed.startsWith('### ')) {
								return (
									<h3
										key={i}
										className="mt-8 mb-3 font-['Futura_Hv_BT'] text-[20px] text-[#32302F]"
									>
										{trimmed.slice(4)}
									</h3>
								)
							}
							if (trimmed.startsWith('| ')) {
								return null
							}
							if (trimmed.startsWith('- **')) {
								const match = trimmed.match(/^- \*\*(.+?)\*\*(.*)$/)
								if (match) {
									return (
										<li key={i} className="my-1 text-[#32302F]">
											<strong>{match[1]}</strong>
											{match[2]}
										</li>
									)
								}
							}
							if (trimmed.startsWith('- ')) {
								return (
									<li key={i} className="my-1 text-[#32302F]">
										{trimmed.slice(2)}
									</li>
								)
							}
							if (/^\d+\./.test(trimmed)) {
								const match = trimmed.match(/^\d+\.\s+(.+)$/)
								return (
									<li key={i} className="my-1 text-[#32302F]">
										{match ? match[1] : trimmed}
									</li>
								)
							}

							return (
								<p key={i} className="mb-4 leading-relaxed text-[#32302F]">
									{renderInlineMarkdown(trimmed)}
								</p>
							)
						})}

						{/* Render tables separately */}
						{renderTables(post.content)}
					</div>
				</div>
			</div>

			{/* Related posts */}
			{relatedPosts.length > 0 && (
				<div className="bg-[#F8F9FA] px-4 py-14 md:px-[100px]">
					<RelatedPosts posts={relatedPosts} />
				</div>
			)}

			{/* CTA */}
			<div className="bg-white px-4 py-16 text-center md:px-[100px]">
				<h2 className="mb-4 font-['Futura_Hv_BT'] text-[28px] text-[#32302F] md:text-[36px]">
					Ready to simplify your rental journey?
				</h2>
				<p className="mx-auto mb-8 max-w-md text-[#747474]">
					Verify once, apply everywhere. Build your trusted rental profile with
					TrueTenant today.
				</p>
				<AnimatedButton
					asChild
					className="inline-flex w-auto rounded-[52px] bg-[#32302F] px-8 text-white hover:bg-[#1a1918]"
				>
					<Link to="/tenant/signup">Get Started Free</Link>
				</AnimatedButton>
			</div>

			<FooterSection />
		</div>
	)
}

function renderInlineMarkdown(text: string) {
	const parts = text.split(/(\*\*[^*]+\*\*)/g)
	return parts.map((part, i) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return <strong key={i}>{part.slice(2, -2)}</strong>
		}
		return part
	})
}

function renderTables(content: string) {
	const tableBlocks = content.match(/(\|.+\|\n)+/g)
	if (!tableBlocks) return null

	return tableBlocks.map((block, tableIdx) => {
		const rows = block.trim().split('\n')
		const headers = rows[0]
			.split('|')
			.filter(Boolean)
			.map((h) => h.trim())
		const bodyRows = rows.slice(2).map((row) =>
			row
				.split('|')
				.filter(Boolean)
				.map((cell) => cell.trim()),
		)

		return (
			<div key={tableIdx} className="my-6 overflow-x-auto">
				<table className="w-full border-collapse text-sm">
					<thead>
						<tr>
							{headers.map((h, i) => (
								<th
									key={i}
									className="border border-[#AEAEAE] bg-[#F8F9FA] p-3 text-left font-semibold text-[#32302F]"
								>
									{h}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{bodyRows.map((row, ri) => (
							<tr key={ri} className="even:bg-[#FAFAFA]">
								{row.map((cell, ci) => (
									<td
										key={ci}
										className="border border-[#AEAEAE] p-3 text-[#32302F]"
									>
										{cell}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		)
	})
}
